import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Calendar, Users, Building2, UserCheck, Car, Zap,
  CreditCard, Calculator, ArrowLeftRight, Settings, Menu, X, Search,
  Bell, LogOut, Plus, ChevronLeft, ChevronRight, ChevronDown, MoreHorizontal, CalendarClock, MessageSquare, FileText, Globe2, ShieldAlert, Radar, MapPin, Plane, Mail, BatteryCharging, Crown, Headset
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import StrianoLogo from "./StrianoLogo";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import BookingModal from "./BookingModal";
import NotificationsPanel from "./NotificationsPanel";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "operatore", "autista"] },
  { to: "/prenotazioni", label: "Prenotazioni", icon: Calendar, roles: ["admin", "operatore", "autista"] },
  { to: "/preventivi", label: "Preventivi", icon: FileText, roles: ["admin", "operatore"] },
  { to: "/calendario", label: "Calendario", icon: Calendar, roles: ["admin", "operatore"] },
  { to: "/clienti", label: "Clienti", icon: Users, roles: ["admin", "operatore"] },
  { to: "/fornitori", label: "Fornitori", icon: Building2, roles: ["admin", "operatore"] },
  { to: "/autisti", label: "Autisti", icon: UserCheck, roles: ["admin", "operatore"] },
  { to: "/flotta", label: "Flotta", icon: Car, roles: ["admin", "operatore"] },
  { to: "/uber", label: "Uber", icon: Zap, roles: ["admin", "operatore"] },
  { to: "/incassi", label: "Incassi", icon: CreditCard, roles: ["admin", "operatore"] },
  { to: "/conteggi", label: "Conteggi", icon: Calculator, roles: ["admin", "operatore"] },
  { to: "/ricariche-ev", label: "Ricariche veicoli elettrici", icon: BatteryCharging, roles: ["admin", "admin_azienda", "operatore"] },
  { to: "/scambio-servizi", label: "Scambio Servizi", icon: ArrowLeftRight, roles: ["admin", "operatore"] },
  { to: "/richieste-driver", label: "Richieste Driver", icon: Headset, roles: ["admin", "admin_azienda", "operatore"] },
  { to: "/richieste-tourvoyagers", label: "Richieste Tourvoyagers", icon: Globe2, roles: ["admin", "operatore"] },
  { to: "/turnistica", label: "Turnistica", icon: CalendarClock, roles: ["admin", "operatore", "autista"] },
  { to: "/sentinel", label: "Sentinel", icon: Radar, roles: ["admin", "admin_azienda", "operatore"] },
  { to: "/gps-live", label: "GPS LIVE", icon: MapPin, roles: ["admin", "admin_azienda", "operatore"] },
  { to: "/airport", label: "Airport Intelligence", icon: Plane, roles: ["admin", "admin_azienda", "operatore"] },
  { to: "/email-ai", label: "Email & Prenotazioni AI", icon: Mail, roles: ["admin", "admin_azienda", "operatore"] },
  { to: "/scadenze", label: "Scadenze", icon: ShieldAlert, roles: ["admin", "admin_azienda", "operatore"] },
  { to: "/messaggi", label: "Messaggi", icon: MessageSquare, roles: ["admin", "operatore"] },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, roles: ["admin", "operatore"] },
];

const MAIN = new Set(["/", "/prenotazioni", "/clienti", "/fornitori", "/autisti", "/flotta", "/uber", "/incassi", "/conteggi", "/scambio-servizi", "/impostazioni"]);

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [reqOpen, setReqOpen] = useState({ open: 0, urgent: 0 });
  const [chatUnread, setChatUnread] = useState(0);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState(null);

  const loadCompany = async () => {
    try {
      const r = await api.get("/settings/company");
      setCompanyLogo(r.data?.logo_url || null);
      setCompanyName((r.data?.company_name || "").trim() || null);
    } catch { setCompanyLogo(null); }
  };

  const loadNotifCount = async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      setNotifCount(r.data?.unread || 0);
    } catch { setNotifCount(0); }
  };
  const loadChatUnread = async () => {
    try {
      const r = await api.get("/chat/unread-count");
      setChatUnread(r.data?.unread || 0);
    } catch { setChatUnread(0); }
  };
  useEffect(() => {
    if (!["admin", "admin_azienda", "operatore"].includes(user?.role)) return;
    const f = () => api.get("/driver-requests/open-count").then(r => setReqOpen(r.data)).catch(() => {});
    f(); const t = setInterval(f, 30000);
    return () => clearInterval(t);
  }, [user?.role]);

  useEffect(() => {
    loadNotifCount();
    loadChatUnread();
    loadCompany();
    const t = setInterval(loadNotifCount, 30000);
    const t2 = setInterval(loadChatUnread, 5000);
    const t3 = setInterval(loadCompany, 60000);
    const onLogo = () => loadCompany();
    window.addEventListener("company-logo-updated", onLogo);
    return () => { clearInterval(t); clearInterval(t2); clearInterval(t3); window.removeEventListener("company-logo-updated", onLogo); };
  }, []);

  useEffect(() => {
    if (!q || q.length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/search", { params: { q } });
        setResults(r.data);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const filteredNav = NAV.filter(n => n.roles.includes(user?.role));
  const loc = useLocation();
  const grouped = user?.role !== "autista" && filteredNav.some(n => !MAIN.has(n.to));
  const mainNav = grouped ? filteredNav.filter(n => MAIN.has(n.to)) : filteredNav;
  const moreNav = grouped ? filteredNav.filter(n => !MAIN.has(n.to)) : [];
  const moreActive = moreNav.some(n => loc.pathname.startsWith(n.to));
  const [moreOpen, setMoreOpen] = useState(moreActive);
  useEffect(() => { if (moreActive) setMoreOpen(true); }, [moreActive]);
  const moreBadge = (moreNav.some(n => n.to === "/richieste-driver") ? reqOpen.open : 0) + (moreNav.some(n => n.to === "/messaggi") ? chatUnread : 0);
  const [isLg, setIsLg] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const f = (e) => setIsLg(e.matches);
    mq.addEventListener("change", f);
    return () => mq.removeEventListener("change", f);
  }, []);
  // Solo Dashboard (non autisti) su desktop: ricerca, notifiche e profilo vanno dentro l'intestazione con la foto
  const heroMode = isLg && loc.pathname === "/" && user?.role !== "autista";

  const SidebarInner = ({ mobile = false }) => (
    <div className="flex flex-col h-full relative">
      <div className="flex items-center justify-between px-4 pt-5 pb-4 border-b border-gold/15">
        {(!collapsed || mobile) ? (
          <div className="flex items-center gap-3 min-w-0">
            {companyLogo ? (
              <div
                data-testid="sidebar-company-logo"
                className="w-[42px] h-[42px] rounded-xl bg-black ring-1 ring-gold/40 shadow-lg shadow-black/60 flex items-center justify-center overflow-hidden flex-shrink-0"
              >
                <img src={companyLogo} alt="Logo" className="w-full h-full object-contain"/>
              </div>
            ) : (
              <div className="flex-shrink-0" aria-hidden="true"><StrianoLogo variant="compact" size={42} /></div>
            )}
            <div className="leading-tight min-w-0">
              <div className="font-display text-[#E9C46A] text-[15px] font-semibold tracking-[0.1em] leading-tight line-clamp-2">
                {(companyName || "STRIANO NCC").toUpperCase()}
              </div>
              <div className="text-[9px] text-[#C9A961]/80 tracking-[0.22em] mt-1">NCC & LUXURY TRANSFER</div>
            </div>
          </div>
        ) : (
          companyLogo ? (
            <div data-testid="sidebar-company-logo-collapsed" className="w-[38px] h-[38px] mx-auto rounded-xl bg-black ring-1 ring-gold/40 flex items-center justify-center overflow-hidden">
              <img src={companyLogo} alt="Logo" className="w-full h-full object-contain"/>
            </div>
          ) : (
            <div className="w-[38px] h-[38px] mx-auto rounded-xl bg-black ring-1 ring-gold/40 flex items-center justify-center" aria-hidden="true">
              <svg viewBox="0 0 48 24" width="26" height="16" fill="none">
                <path d="M4 17 Q6 11 12 10 L18 7 Q22 6 28 6 L34 6 Q39 7 42 11 L45 12 Q46 14 45 16 L42 17 Q41 18 39 18 L34 18 Q33 19 32 19 Q31 19 30 18 L18 18 Q17 19 16 19 Q15 19 14 18 L9 18 Q6 18 4 17 Z" fill="#D6A62A"/>
              </svg>
            </div>
          )
        )}
        {mobile ? (
          <button data-testid="mobile-sidebar-close" onClick={() => setMobileOpen(false)} className="text-gold/70 hover:text-gold">
            <X size={20} />
          </button>
        ) : (!collapsed) ? (
          <button data-testid="sidebar-toggle" onClick={() => setCollapsed(!collapsed)} className="text-gold/60 hover:text-gold">
            <ChevronLeft size={18} />
          </button>
        ) : null}
      </div>
      {collapsed && !mobile && (
        <button data-testid="sidebar-toggle" onClick={() => setCollapsed(false)} className="mx-auto my-1 text-gold/60 hover:text-gold">
          <ChevronRight size={16} />
        </button>
      )}

      <div className="p-3">
        <button
          data-testid="new-booking-sidebar-btn"
          onClick={() => { setShowBooking(true); if (mobile) setMobileOpen(false); }}
          className="w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-black font-bold px-3 py-2.5 rounded-xl shadow-[0_8px_28px_-8px_rgba(212,167,44,0.6)] border border-gold-light/60 transition-all text-sm uppercase tracking-wide"
        >
          <Plus size={16} strokeWidth={3} />
          {(!collapsed || mobile) && <span>Nuova Prenotazione</span>}
        </button>
      </div>

      <nav className="flex-1 px-2 pb-3 space-y-0.5 overflow-y-auto">
        {[...mainNav, ...(grouped ? [{ more: true }] : []), ...(moreOpen ? moreNav.map(n => ({ ...n, sub: true })) : [])].map((n) => n.more ? (
          <button key="__more" type="button" data-testid="sidebar-more-toggle" onClick={() => setMoreOpen(o => !o)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm border border-transparent transition-colors ${moreActive ? "text-[#F6E3A8]" : "text-slate-300 hover:text-white hover:bg-white/[0.04]"}`}>
            <MoreHorizontal size={18} className="shrink-0 text-[#E9C46A]" />
            {(!collapsed || mobile) && <span className="font-medium flex-1 text-left">Altro</span>}
            {moreBadge > 0 && !moreOpen && <span data-testid="sidebar-more-badge" className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#E9C46A] text-black text-[10px] font-extrabold flex items-center justify-center">{moreBadge > 99 ? "99+" : moreBadge}</span>}
            {(!collapsed || mobile) && <ChevronDown size={15} className={`text-[#E9C46A]/80 transition-transform ${moreOpen ? "rotate-180" : ""}`} />}
          </button>
        ) : (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={`sidebar-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            title={n.label}
            onClick={() => mobile && setMobileOpen(false)}
            className={({ isActive }) =>
              `group flex items-center gap-3 ${n.sub && (!collapsed || mobile) ? "pl-6 pr-3 py-2 text-[13px]" : "px-3 py-2.5 text-sm"} rounded-lg transition-all ${
                isActive
                  ? "bg-gradient-to-r from-[#D4A72C]/35 via-[#D4A72C]/15 to-[#D4A72C]/5 text-[#F6E3A8] border border-[#D4A72C]/55 shadow-[0_0_18px_-8px_rgba(212,167,44,0.6)]"
                  : "text-slate-300 hover:text-white hover:bg-white/[0.04] border border-transparent"
              }`
            }
          >
            <n.icon size={18} className="shrink-0 text-[#E9C46A]/90" />
            {(!collapsed || mobile) && <span className="font-medium flex-1">{n.label}</span>}
            {n.to === "/richieste-driver" && reqOpen.open > 0 && (
              <span data-testid="sidebar-requests-badge" className={`ml-auto min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-extrabold flex items-center justify-center ${reqOpen.urgent ? "bg-rose-500 text-white" : "bg-[#E9C46A] text-black"}`}>
                {reqOpen.open > 99 ? "99+" : reqOpen.open}
              </span>
            )}
            {n.to === "/messaggi" && chatUnread > 0 && (
              <span data-testid="sidebar-chat-badge" className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-slate-950 text-[10px] font-extrabold flex items-center justify-center">
                {chatUnread > 99 ? "99+" : chatUnread}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {(!collapsed || mobile) && (
        <div className="px-3 pb-3 hidden [@media(min-height:1100px)]:block" data-testid="sidebar-promo">
          <div className="relative h-[150px] rounded-xl overflow-hidden border border-[#D4A72C]/25">
            <img src="/img/rome-side.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            <div className="absolute left-3 bottom-3 text-[12px] leading-snug text-white font-display">Servizio<br />Qualità<br />Sicurezza<br /><span className="text-[#E9C46A]">Sempre con te.</span></div>
          </div>
        </div>
      )}
      {false && (
        <div className="relative px-5 pb-3 pt-2 border-t border-gold/15" aria-hidden="true">
          <div className="relative h-[88px] rounded-xl overflow-hidden opacity-80">
            <svg viewBox="0 0 220 96" width="100%" height="100%" preserveAspectRatio="xMidYMax meet">
              <defs>
                <linearGradient id="colGold" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F1C75B"/>
                  <stop offset="60%" stopColor="#C99A24"/>
                  <stop offset="100%" stopColor="#5A4310"/>
                </linearGradient>
                <linearGradient id="colFloor" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(214,166,42,0.25)"/>
                  <stop offset="100%" stopColor="rgba(0,0,0,0)"/>
                </linearGradient>
              </defs>
              {/* Colosseo stilizzato */}
              <g fill="url(#colGold)" opacity="0.9">
                <ellipse cx="110" cy="68" rx="80" ry="12" fill="url(#colFloor)" opacity="0.6"/>
                <path d="M30 70 Q30 42 110 32 Q190 42 190 70 L190 72 L30 72 Z" opacity="0.9"/>
                {/* archi */}
                {Array.from({length: 11}).map((_, i) => {
                  const x = 38 + i*13;
                  return <rect key={i} x={x} y={48} width="7" height="22" rx="3.5" fill="#000" opacity="0.55"/>;
                })}
                {Array.from({length: 11}).map((_, i) => {
                  const x = 38 + i*13;
                  return <rect key={`r${i}`} x={x+1.5} y={38} width="4" height="10" rx="2" fill="#000" opacity="0.4"/>;
                })}
              </g>
              {/* Silhouette auto luxury */}
              <g transform="translate(140, 54)">
                <path d="M0 18 Q2 10 10 9 L18 5 Q24 3 32 3 L40 3 Q48 4 52 10 L58 12 Q60 15 58 17 L54 18 Q53 19 51 19 L44 19 Q43 20 42 20 Q40 20 39 19 L18 19 Q17 20 16 20 Q14 20 13 19 L6 19 Q2 19 0 18 Z" fill="url(#colGold)"/>
                <circle cx="14" cy="19" r="3" fill="#000"/>
                <circle cx="44" cy="19" r="3" fill="#000"/>
              </g>
            </svg>
          </div>
          <div className="text-center mt-2">
            <div className="text-[9px] font-mono-num text-gold/70 tracking-[0.4em]">NCC</div>
            <div className="text-[9px] font-mono-num text-txt-2/50 tracking-[0.35em] mt-1">
              VIAGGI · ESPERIENZE · PERSONE
            </div>
          </div>
        </div>
      )}

      <div className="p-3 border-t border-gold/15">
        <div className={`flex items-center gap-3 ${collapsed && !mobile ? "justify-center" : ""}`}>
          <div className="w-9 h-9 rounded-full bg-gold/15 border border-gold/40 text-gold flex items-center justify-center font-bold text-sm shadow-[0_0_14px_-4px_rgba(214,166,42,0.5)]">
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </div>
          {(!collapsed || mobile) && (
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate font-semibold">{user?.name}</div>
              <div className="text-[10px] text-gold/70 uppercase tracking-[0.2em]">{user?.role}</div>
            </div>
          )}
          <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); toast.success("Disconnesso"); }} className="text-gold/60 hover:text-rose-400 transition-colors" title="Esci">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  const searchBox = (
            <div className="relative flex-1 max-w-xl">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold/70" />
              <Input
                data-testid="global-search-input"
                placeholder="Cerca prenotazione, cliente, autista, targa..."
                className="pl-9 bg-luxe-card border-gold/25 text-slate-200 placeholder:text-slate-500 focus-visible:ring-gold/50"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {results && (
                <div className="absolute top-full mt-2 left-0 right-0 bg-slate-950 border border-slate-800 rounded-xl shadow-2xl p-2 max-h-96 overflow-y-auto z-50">
                  {["bookings","customers","drivers","vehicles","suppliers"].map((k) => (
                    results[k]?.length > 0 && (
                      <div key={k} className="mb-2">
                        <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{k}</div>
                        {results[k].map((it, i) => (
                          <div key={i} className="px-2 py-1.5 hover:bg-slate-900 rounded text-sm text-slate-300 cursor-pointer">
                            {it.booking_number || it.name || it.plate || `${it.first_name || ""} ${it.last_name || ""}`}
                            <span className="text-slate-500 ml-2 text-xs">{it.passenger_name || it.destination || it.email || it.phone || ""}</span>
                          </div>
                        ))}
                      </div>
                    )
                  ))}
                  {Object.values(results).every(v => (v || []).length === 0) && (
                    <div className="px-2 py-3 text-sm text-slate-500">Nessun risultato</div>
                  )}
                </div>
              )}
            </div>
  );
  const bellBtn = (
            <button className="relative w-10 h-10 shrink-0 rounded-full bg-luxe-card border border-gold/25 text-gold/80 hover:text-gold hover:border-gold/60 transition flex items-center justify-center" title="Notifiche" onClick={() => setNotifOpen(true)} data-testid="notif-bell">
              <Bell size={18} />
              {notifCount > 0 && (
                <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center shadow-lg shadow-rose-500/40">{notifCount > 99 ? "99+" : notifCount}</span>
              )}
            </button>
  );
  const profileCard = (
            <div className="hidden md:flex items-center gap-2.5 pl-3 pr-1 py-1 rounded-full bg-luxe-card border border-gold/20 shrink-0" data-testid="header-profile">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold-light to-gold-dark text-black font-extrabold text-[11px] flex items-center justify-center">
                {(user?.name || "?").split(" ").map(x=>x[0]).slice(0,2).join("").toUpperCase()}
              </div>
              <div className="leading-tight pr-2">
                <div className="text-white text-[12px] font-semibold truncate max-w-[140px]">{user?.name || user?.email}</div>
                <div className="text-gold/70 text-[9px] uppercase tracking-[0.2em]">
                  {user?.role === "admin" ? "Amministratore" : user?.role === "operatore" ? "Operatore" : user?.role === "autista" ? "Autista" : user?.role}
                </div>
              </div>
            </div>
  );

  return (
    <div className="min-h-screen bg-luxe-base bg-grid">
      {/* Desktop Sidebar */}
      <aside
        data-testid="desktop-sidebar"
        className={`hidden lg:block fixed inset-y-0 left-0 z-30 mb-side border-r border-white/[0.07] transition-all duration-200 ${collapsed ? "w-[76px]" : "w-64"}`}
      >
        <SidebarInner />
      </aside>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/80" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 mb-side border-r border-white/10 pt-safe">
            <SidebarInner mobile />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className={`transition-all ${collapsed ? "lg:pl-[76px]" : "lg:pl-64"}`}>
        {!heroMode && <header className="sticky top-0 z-20 bg-[#050505]/92 backdrop-blur-md border-b border-[#D4A72C]/15 pt-safe">
          <div className="flex items-center gap-3 px-4 lg:px-6 h-16">
            <button type="button" aria-label="Apri menu" data-testid="mobile-menu-btn" className="lg:hidden relative z-10 -ml-2 w-11 h-11 shrink-0 flex items-center justify-center text-slate-300 touch-manipulation" onClick={() => setMobileOpen(true)}>
              <Menu size={22} />
            </button>
            {searchBox}
            <Button
              data-testid="new-booking-header-btn"
              onClick={() => setShowBooking(true)}
              className="hidden sm:inline-flex btn-gold-primary font-bold gap-1.5 h-10 px-4 rounded-xl"
            >
              <Plus size={16} strokeWidth={3} />
              Nuova
            </Button>
            {bellBtn}
            {profileCard}
          </div>
        </header>}

        <main className="p-4 lg:p-8">
          <Outlet context={{ openBooking: () => setShowBooking(true), heroTools: heroMode ? <>{searchBox}{bellBtn}{profileCard}</> : null }} />
        </main>
      </div>

      {showBooking && (
        <BookingModal onClose={(saved) => { setShowBooking(false); if (saved) toast.success("Prenotazione salvata"); }} />
      )}
      {notifOpen && (
        <NotificationsPanel onClose={() => { setNotifOpen(false); loadNotifCount(); }} onNavigate={(path) => { setNotifOpen(false); nav(path); loadNotifCount(); }} />
      )}
    </div>
  );
}
